import { useCallback, useState } from 'react'

export type TransactionRuleStatus = 'active' | 'archived' | 'all'

export type TransactionRuleScope = 'all' | 'local' | 'global'

export type TransactionRuleFilter = {
  status?: TransactionRuleStatus
  scope?: TransactionRuleScope
  ruleUse?: string
  ruleText?: string
  ruleType?: string
  transactionCategoryType?: string
  transactionCategoryId?: number
  userId?: number
}

export type FilterInputs = {
  scope: TransactionRuleScope
  ruleUse: string
  ruleText: string
  ruleType: string
  transactionCategoryType: string
  transactionCategoryId: string
  userId: string
}

const INITIAL_FILTER_INPUTS: FilterInputs = {
  scope: 'all',
  ruleUse: '',
  ruleText: '',
  ruleType: '',
  transactionCategoryType: '',
  transactionCategoryId: '',
  userId: '',
}

export const useTransactionRuleFilters = (status: TransactionRuleStatus) => {
  const [filters, setFilters] = useState<Omit<TransactionRuleFilter, 'status'>>(
    {}
  )
  const [filterInputs, setFilterInputs] = useState<FilterInputs>(
    INITIAL_FILTER_INPUTS
  )

  const buildFilter = useCallback((): TransactionRuleFilter => {
    const filter: TransactionRuleFilter = { status }
    if (filters.scope && filters.scope !== 'all') filter.scope = filters.scope
    if (filters.ruleUse) filter.ruleUse = filters.ruleUse
    if (filters.ruleText) filter.ruleText = filters.ruleText
    if (filters.ruleType) filter.ruleType = filters.ruleType
    if (filters.transactionCategoryType)
      filter.transactionCategoryType = filters.transactionCategoryType
    if (filters.transactionCategoryId)
      filter.transactionCategoryId = filters.transactionCategoryId
    // A specific userId overrides scope='global' (global means no user), so
    // only forward userId when scope is not 'global'.
    if (filters.userId && filters.scope !== 'global')
      filter.userId = filters.userId
    return filter
  }, [status, filters])

  const applyFilters = useCallback(() => {
    const newFilters: Omit<TransactionRuleFilter, 'status'> = {}
    if (filterInputs.scope && filterInputs.scope !== 'all')
      newFilters.scope = filterInputs.scope
    if (filterInputs.ruleUse) newFilters.ruleUse = filterInputs.ruleUse
    if (filterInputs.ruleText) newFilters.ruleText = filterInputs.ruleText
    if (filterInputs.ruleType) newFilters.ruleType = filterInputs.ruleType
    if (filterInputs.transactionCategoryType)
      newFilters.transactionCategoryType = filterInputs.transactionCategoryType
    if (filterInputs.transactionCategoryId)
      newFilters.transactionCategoryId = Number(
        filterInputs.transactionCategoryId
      )
    if (filterInputs.userId && filterInputs.scope !== 'global')
      newFilters.userId = Number(filterInputs.userId)
    setFilters(newFilters)
  }, [filterInputs])

  const clearFilters = useCallback(() => {
    setFilterInputs(INITIAL_FILTER_INPUTS)
    setFilters({})
  }, [])

  const updateFilterInput = useCallback(
    <K extends keyof FilterInputs>(key: K, value: FilterInputs[K]) => {
      setFilterInputs((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  const hasActiveFilters = Object.keys(filters).length > 0

  return {
    filters,
    filterInputs,
    buildFilter,
    applyFilters,
    clearFilters,
    updateFilterInput,
    hasActiveFilters,
  }
}
